package components

import hide
import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.dom.create
import kotlinx.html.js.div
import mainScope
import net.gorillagroove.api.UserId
import net.gorillagroove.history.SiteStats
import net.gorillagroove.history.UserStatsResponse
import org.jetbrains.letsPlot.Stat
import org.jetbrains.letsPlot.frontend.JsFrontendUtil
import org.jetbrains.letsPlot.geom.geomBar
import org.jetbrains.letsPlot.geom.geomPie
import org.jetbrains.letsPlot.intern.Plot
import org.jetbrains.letsPlot.label.ggtitle
import org.jetbrains.letsPlot.letsPlot
import org.jetbrains.letsPlot.themes.elementBlank
import org.jetbrains.letsPlot.themes.theme
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import query
import queryId
import show
import kotlin.math.roundToInt
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds

private val loadingSpinner get() = document.queryId<HTMLElement>("year-in-review-loader")
private val pageSubtitle get() = document.queryId<HTMLElement>("page-subtitle")

@Suppress("FunctionName")
fun UserYearInReview(): HTMLDivElement {
    return document.create.div {
        id = "user-stats"

        div("year-review-header") {
            h1 { }
            p("subtitle") {
                id = "page-subtitle"
                +"Processing all of your groovy data. Hang tight..."
            }
        }

        LoadingSpinner(id = "year-in-review-loader", classes = "nav-text-color", scale = 2.0)

        div("stats-grid") {
            id = "stat-cards"
        }

        div {
            id = "year-in-review-content"
        }

        mainScope.launch {
            val userId = PageRouter.currentPathVariables.getValue("userId")
                .toLong()
                .let { UserId(it) }
            populateData(userId)
        }
    }
}

private fun populateData(userId: UserId) = mainScope.launch {
    loadingSpinner.show()

    val year = PageRouter.currentPathVariables.getValue("year").toInt()

    val stats = SiteStats.getYearSummary(userId, year, PageRouter.getQueryParam("at"))

    pageSubtitle.innerText = "Wow what a year. Stuff happened."
    loadingSpinner.hide()

    val usernameField = document.query<HTMLElement>("#user-stats h1")
    usernameField.innerText = "${stats.username}'s $year Grooving"

    // Populate stat cards
    val statsContainer = document.queryId<HTMLElement>("stat-cards")
    statsContainer.innerHTML = ""
    statsContainer.append {
        div("stat-card") {
            div("stat-number") {
                +"${stats.totalTimeListened.seconds.inWholeHours}"
            }
            div("stat-label") {
                +"Hours Listened"
            }
        }

        div("stat-card rank-card") {
            div("stat-number") {
                +"#${stats.playtimePlacement}"
            }
            div("stat-label") {
                +"Site Ranking"
            }
            div("stat-sublabel") {
                val percent = (stats.playtimePercent * 100).roundToInt()
                +"$percent% of time listened"
            }
        }

        div("stat-card") {
            div("stat-number") {
                +"${stats.totalTracksListened}"
            }
            div("stat-label") {
                +"Track Listens"
            }
            div("stat-sublabel") {
                +"to ${stats.uniqueTracksListened} unique tracks"
            }

        }

        div("stat-card") {
            div("stat-number") {
                +"${stats.newTrackCount}"
            }
            div("stat-label") {
                +"Tracks Added"
            }
        }
    }

    val contentContainer = document.queryId<HTMLElement>("year-in-review-content")
    contentContainer.innerHTML = ""

    contentContainer.append {
        div("content-grid") {
            div("card") {
                h2 { +"Top Songs" }

                stats.mostListenedTracks.entries.forEachIndexed { index, (track, plays) ->
                    div("list-item") {
                        div("rank") {
                            +"#${index + 1}"
                        }
                        div("item-info") {
                            div("item-name") {
                                +track
                            }
                            div("progress-bar") {
                                val maxPlays = stats.mostListenedTracks.maxOfOrNull { it.value } ?: 1
                                val percentage = (plays.toDouble() / maxPlays * 100).roundToInt()
                                div("progress-fill") {
                                    style = "width: $percentage%"
                                }
                            }
                        }
                        div("item-count") {
                            +"$plays plays"
                        }
                    }
                }
            }

            div("card") {
                h2 { +"Top Artists" }

                stats.mostListenedArtists.entries.forEachIndexed { index, (artist, plays) ->
                    div("list-item") {
                        div("rank") {
                            +"#${index + 1}"
                        }
                        div("item-info") {
                            div("item-name") {
                                +artist
                            }
                            div("progress-bar") {
                                val maxPlays = stats.mostListenedArtists.maxOfOrNull { it.value } ?: 1
                                val percentage = (plays.toDouble() / maxPlays * 100).roundToInt()
                                div("progress-fill") {
                                    style = "width: $percentage%"
                                }
                            }
                        }
                        div("item-count") {
                            +"$plays plays"
                        }
                    }
                }
            }

            div("card") {
                h2 { +"Top Albums" }

                stats.mostListenedAlbums.entries.forEachIndexed { index, (album, plays) ->
                    div("list-item") {
                        div("rank") {
                            +"#${index + 1}"
                        }
                        div("item-info") {
                            div("item-name") {
                                +album
                            }
                            div("progress-bar") {
                                val maxPlays = stats.mostListenedAlbums.maxOfOrNull { it.value } ?: 1
                                val percentage = (plays.toDouble() / maxPlays * 100).roundToInt()
                                div("progress-fill") {
                                    style = "width: $percentage%"
                                }
                            }
                        }
                        div("item-count") {
                            +"$plays plays"
                        }
                    }
                }
            }
        }
    }

    val middleGrid = document.create.div("content-grid") {  }
    middleGrid.append(createChartContainer(createDayPlot(stats), "Listening by Day of Week"))
    middleGrid.append(createChartContainer(createMonthPlot(stats), "Listening Over Time"))
    contentContainer.appendChild(middleGrid)

    val bottomGrid = document.create.div("content-grid")
    bottomGrid.appendChild(createChartContainer(createHourPlot(stats), "Daily Listening Pattern"))
    bottomGrid.appendChild(createChartContainer(createDeviceTypePlot(stats), "Device Usage"))
    contentContainer.appendChild(bottomGrid)
}

private fun createDayPlot(stats: UserStatsResponse): HTMLDivElement {
    val data = mapOf(
        "Day of Week" to listOf("Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"),
        "Hours Listened" to stats.timeListeningByDay.map { it.seconds.toGraphUnit() },
    )

    val plot = letsPlot(data) {
        x = "Day of Week"
        y = "Hours Listened"
    } + geomBar(stat = Stat.identity)

    return createPlotDiv(plot)
}

private fun createMonthPlot(stats: UserStatsResponse): HTMLDivElement {
    val data = mapOf(
        "Month of Year" to listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"),
        "Hours Listened" to stats.timeListeningByMonth.map { it.seconds.toGraphUnit() },
    )

    val plot = letsPlot(data) {
        x = "Month of Year"
        y = "Hours Listened"
    } + geomBar(stat = Stat.identity)

    return createPlotDiv(plot)
}

private fun createHourPlot(stats: UserStatsResponse): HTMLDivElement {
    val data = mapOf(
        "Hour of Day" to listOf("12AM", "1AM", "2AM", "3AM", "4AM", "5AM", "6AM", "7AM", "8AM", "9AM", "10AM", "11AM", "12PM", "1PM", "2PM", "3PM", "4PM", "5PM", "6PM", "7PM", "8PM", "9PM", "10PM", "11PM"),
        "Hours Listened" to stats.timeListeningByHour.map { it.seconds.toGraphUnit() },
    )

    val plot = letsPlot(data) {
        x = "Hour of Day"
        y = "Hours Listened"
    } + geomBar(stat = Stat.identity)

    return createPlotDiv(plot)
}

private fun createDeviceTypePlot(stats: UserStatsResponse): HTMLDivElement {
    val data = mapOf(
        "Device Types" to stats.timeListenedByDeviceType.keys.map { it.displayName },
        "Hours Listened" to stats.timeListenedByDeviceType.map { it.value.seconds.toGraphUnit() },
    )

    val plot = letsPlot(data) + geomPie(stat = Stat.identity, size = 25) {
        fill = "Device Types"
        slice = "Hours Listened"
    } + theme(
        axisLine = elementBlank(),
        axisTitle = elementBlank(),
        axisTicks = elementBlank(),
        axisText = elementBlank(),
        axisTextX = elementBlank(),
        axisTextY = elementBlank(),
        stripBackground = elementBlank(),
        panelGrid = elementBlank(),
    ) + ggtitle(title = "Hours by Device Type")

    return createPlotDiv(plot)
}

private fun Duration.toGraphUnit(): Long {
    return this.inWholeHours
}

private fun createPlotDiv(plot: Plot): HTMLDivElement {
    return JsFrontendUtil.createPlotDiv(plot).also {
        it.classList.add("chart-plot")
    }
}

private fun createChartContainer(plotDiv: HTMLDivElement, title: String): HTMLDivElement {
    return document.create.div("chart-container") {
        h2 { +title }
    }.also { container ->
        container.appendChild(plotDiv)
    }
}
